import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Permission } from "@/shared/auth/permissions";
import { usePermission } from "@/shared/auth/usePermission";
import { normalizeOrganizationId } from "@/shared/organizations/organization-id";
import { OrganizationHeader } from "./OrganizationHeader";
import { OrganizationInvitationsPanel } from "./OrganizationInvitationsPanel";
import { OrganizationMembersSection } from "./OrganizationMembersSection";
import { organizationsErrorKind } from "./organizations-error";
import { useOrganization } from "./use-organization";

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link
      to="/organizations"
      data-testid="organization-back"
      className="inline-flex items-center gap-1 font-sans text-sm text-primary underline-offset-2 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {t("organizations.backToList")}
    </Link>
  );
}

function NotFound({
  messageKey,
  testId,
}: {
  readonly messageKey: string;
  readonly testId: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <BackLink />
      <h1 className="text-4xl">{t("organizations.title")}</h1>
      <p role="alert" data-testid={testId} className="font-sans text-sm text-muted-foreground">
        {t(messageKey)}
      </p>
    </div>
  );
}

function OrganizationDetailContent({ organizationId }: { readonly organizationId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useOrganization(organizationId);
  const canRename = usePermission(Permission.ORG_NAME_EDIT);
  const canManageAdmins = usePermission(Permission.ORG_ADMINS_MANAGE);
  const canManageMembers = usePermission(Permission.ORG_MEMBERS_MANAGE);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label={t("organizations.loading")}
        data-testid="organization-loading"
        className="space-y-3"
      >
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    const kind = organizationsErrorKind(error);
    if (kind === "notFound")
      return (
        <NotFound messageKey="organizations.errors.notFound" testId="organization-not-found" />
      );
    return (
      <div className="space-y-3">
        <BackLink />
        <div
          role="alert"
          data-testid="organization-error"
          className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t(`organizations.errors.${kind}`)}
          </p>
          {kind !== "forbidden" && (
            <Button
              data-testid="organization-retry"
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
            >
              {t("organizations.retry")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="organization-detail-screen" className="space-y-6">
      <BackLink />
      <OrganizationHeader organization={data} canRename={canRename} />
      <OrganizationMembersSection
        organizationId={organizationId}
        canManageAdmins={canManageAdmins}
        canManageMembers={canManageMembers}
      />
      <OrganizationInvitationsPanel organizationId={organizationId} />
    </div>
  );
}

/**
 * Organization page for internal staff (ADR-0017). Mount it behind
 * `org.members.read`. The members and invitations panels mount only once the
 * organization loaded, so an unknown id (404 `ORGANIZATION_NOT_FOUND`) fires no
 * further read; a 403 and any other failure get their own copy, never the
 * server's message.
 */
export function OrganizationDetailPage() {
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  // An id the API would drop (blank or over its cap) must never reach the
  // invitations list: without the filter it would return EVERY organization's.
  const validId = normalizeOrganizationId(organizationId);

  if (!validId)
    return <NotFound messageKey="organizations.invalidLink" testId="organization-invalid-link" />;
  return <OrganizationDetailContent key={validId} organizationId={validId} />;
}
